---
title: "Lending and returns"
slug: "lending-and-returns"
category: "operations"
description: "The scan-and-go workflow that parent helpers and TAs can run without training."
tags: ["lend", "return", "loan", "scan", "workflow"]
order: 1
---

Lending and returns is the thing BookBurrow is built around. The whole app has been designed so that a helper with no training can sit down, look at the screen for three seconds, and know exactly what to do. This guide is the tour.

Before you run a full lending session with real pupils, make sure your [catalogue has at least a few books scanned in](/help/guides/scanning-books-isbn-lookup) and that your [barcode scanner is working properly](/help/guides/barcode-scanner-not-working) — nothing kills the rhythm of a busy morning like a scanner that only works half the time.

## The Lend screen

Click **Lend** in the sidebar. You'll see one big input, some borrower info, and a short history of today's loans. That's it — no other buttons, no distractions.

The screen is in one of two states at any moment:

1. **Waiting for a borrower.** The input says "Scan a borrower card or type a card number". This is where every loan starts.
2. **Waiting for a book.** Once a borrower is chosen, the input says "Scan a book to lend to [name]". The borrower's info (year group, any current loans, any warnings) appears above the input.

Scanning two barcodes — one pupil, one book — completes a loan. That's the entire thing.

## A complete lending session

<Steps>
  <Step title="Open BookBurrow and go to Lend">
    If a helper is using it, this is usually already open when they arrive.
  </Step>
  <Step title="Scan the first pupil's card">
    The pupil's name appears, along with any books they currently have on loan and any overdue warnings.
  </Step>
  <Step title="Scan the book(s) they want to take">
    Each scan confirms the book was lent. The borrower stays "active" so you can lend multiple books to the same pupil in a row.
  </Step>
  <Step title="Scan the next pupil's card">
    BookBurrow detects that a card was scanned and switches to the new pupil automatically. No "Done" button needed.
  </Step>
  <Step title="Carry on until the queue is empty">
    The Lend screen can keep going indefinitely. Every transaction is saved immediately.
  </Step>
</Steps>

## The Return screen

Return is even simpler: one input, one message. Scan a book, and it's returned. BookBurrow tells you who it came from, whether it's overdue, and whether anything needs attention (damage, lost-then-found, overdue by more than a week).

<Callout variant="tip" title="Returns don't need a borrower">
You don't scan the borrower's card on return. The book record already knows who it was on loan to, so scanning the book is enough. This is why returns are literally twice as fast as lending.
</Callout>

## Common situations

### A pupil has forgotten their card

Click **Search by name** above the input on the Lend screen. Type the first few letters of the pupil's name, pick them from the list, and carry on as normal. BookBurrow will also show a little warning on the loan history so you can remind the pupil next time.

### A book has no barcode

If a visiting book or a legacy book doesn't have a scannable barcode, click **Search by title** to find it in the catalogue, then tick it as lent. This path is slower, which is a gentle nudge to label every book eventually.

### Lending more books than the limit

If your school has a limit (say, three books per pupil at a time) and a pupil already has three, BookBurrow will show a red warning instead of silently lending the fourth. An admin PIN unlocks the limit for that single loan — useful when a teacher wants to take home a whole topic set.

### Reserving a book

From any book's record, click **Reserve** and pick a pupil. When that book is returned, a notification on the Return screen reminds you to put it aside. This is useful for popular reading scheme books and teacher requests.

## Ending the session

You don't formally "end" a lending session in BookBurrow — you just close the app (or leave it running). All loans are saved instantly, so nothing is lost if the computer crashes or loses power mid-session.

If you want to see what was lent in this sitting, **Reports → Today's activity** gives you the full list, filterable by helper if you've set up named helper accounts. For an overview of everything you can get out of the reports tab, see [Running reports](/help/guides/running-reports).
